/**
 * Per-user appearance and timezone settings, plus the one tenant-wide setting
 * an administrator holds over everybody else.
 */
import React, { useEffect, useMemo, useState } from "react";

import {
  fetchActorVisibility,
  fetchTimezones,
  saveActorVisibility,
  type ActorVisibilitySetting,
} from "../api/client";
import { useRoles } from "../auth/access";
import { usePreferences, useDetectedTimezone } from "../preferences/PreferencesProvider";
import { THEMES, type ThemeId } from "../theme/themes";
import {
  AUTO_TIMEZONE,
  formatDateTimeWithZone,
  offsetLabel,
  timezoneAbbreviation,
  type DateFormatId,
  type FormatContext,
  type TimeFormatId,
} from "../utils/datetime";

const DATE_FORMAT_OPTIONS: Array<{ id: DateFormatId; label: string }> = [
  { id: "iso", label: "2026-09-07  (ISO 8601)" },
  { id: "dmy", label: "07/09/2026  (day/month/year)" },
  { id: "mdy", label: "09/07/2026  (month/day/year)" },
  { id: "medium", label: "7 Sep 2026" },
];

const TIME_FORMAT_OPTIONS: Array<{ id: TimeFormatId; label: string }> = [
  { id: "24h", label: "14:30  (24-hour)" },
  { id: "12h", label: "2:30 pm  (12-hour)" },
];

/** Zones offered at the top of the list, since this is an Australian tenant. */
const SUGGESTED_ZONES = [
  "Australia/Sydney",
  "Australia/Melbourne",
  "Australia/Brisbane",
  "Australia/Adelaide",
  "Australia/Perth",
  "Australia/Darwin",
  "Australia/Hobart",
  "Pacific/Auckland",
  "Asia/Singapore",
  "Asia/Kuala_Lumpur",
  "Europe/London",
  "America/New_York",
  "UTC",
];

function Section({
  title,
  description,
  children,
}: {
  title: string;
  description: string;
  children: React.ReactNode;
}) {
  return (
    <section className="rounded-xl border border-edge bg-surface-raised p-6">
      <h2 className="text-base font-semibold text-content">{title}</h2>
      <p className="mt-1 text-sm text-content-muted">{description}</p>
      <div className="mt-5">{children}</div>
    </section>
  );
}

/**
 * The tenant-wide filter, shown only to administrators.
 *
 * Kept in its own component so the admin-only API calls live behind the same
 * condition that renders it — a manager's browser never issues a request that
 * would come back 403.
 */
function ServiceIdentitySection({ format }: { format: FormatContext }) {
  const [setting, setSetting] = useState<ActorVisibilitySetting | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetchActorVisibility();
        if (!cancelled) setSetting(res);
      } catch {
        if (!cancelled) setError("Could not load the current setting.");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  async function choose(enabled: boolean) {
    if (!setting || enabled === setting.enabled) return;
    setSaving(true);
    setError(null);
    try {
      setSetting(await saveActorVisibility(enabled));
    } catch {
      // Deliberately not optimistic: the toggle keeps showing what is in
      // force, so a failed save cannot leave the screen claiming a filter is
      // off while the API is still applying it.
      setError("Could not save the change. The setting is unchanged.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <Section
      title="Service account activity"
      description="Applies to everyone without the Admin role. Administrators always see this application's own activity, which is how collection problems get diagnosed."
    >
      {error && (
        <p className="mb-3 text-sm text-warning-fg" role="alert">
          {error}
        </p>
      )}

      {setting === null && !error ? (
        <p className="text-sm text-content-muted">Loading…</p>
      ) : setting === null ? null : (
        <>
          <div className="grid gap-3 sm:grid-cols-2">
            {[
              {
                enabled: true,
                label: "Hide it (recommended)",
                detail:
                  "The collector's own reads, workspace membership and artifact attribution are removed for every non-administrator, leaving only the people they are reviewing.",
              },
              {
                enabled: false,
                label: "Show it to everyone",
                detail:
                  "Every role sees the same rows an administrator sees, including the service account's continuous activity across every monitored workspace.",
              },
            ].map((opt) => {
              const selected = setting.enabled === opt.enabled;
              return (
                <button
                  key={String(opt.enabled)}
                  type="button"
                  aria-pressed={selected}
                  disabled={saving}
                  onClick={() => choose(opt.enabled)}
                  className={`rounded-xl border p-4 text-left transition-colors disabled:opacity-60 ${
                    selected
                      ? "border-accent bg-accent-subtle"
                      : "border-edge bg-surface hover:bg-surface-hover"
                  }`}
                >
                  <div className="text-sm font-semibold text-content">{opt.label}</div>
                  <div className="mt-1 text-xs text-content-muted">{opt.detail}</div>
                </button>
              );
            })}
          </div>

          {setting.identityCount === 0 && (
            <p className="mt-4 text-sm text-warning-fg" role="alert">
              No service identity could be read from the Entra secret, so this setting currently
              hides nothing whichever way it is set.
            </p>
          )}

          <dl className="mt-4 space-y-1.5 text-sm">
            <div className="flex justify-between gap-4">
              <dt className="text-content-muted">Last changed by</dt>
              <dd className="text-content">
                {setting.everSet && setting.updatedBy ? (
                  setting.updatedBy
                ) : (
                  <span className="text-content-muted italic">
                    Never changed — hidden by default
                  </span>
                )}
              </dd>
            </div>
            {setting.everSet && setting.updatedAt && (
              <div className="flex justify-between gap-4">
                <dt className="text-content-muted">Changed at</dt>
                <dd className="font-mono text-content">
                  {formatDateTimeWithZone(new Date(setting.updatedAt), format, true)}
                </dd>
              </div>
            )}
          </dl>
        </>
      )}
    </Section>
  );
}

export default function SettingsPage() {
  const { preferences, effectiveTimezone, format, error, clockSkewMs, update, reset } =
    usePreferences();
  const detected = useDetectedTimezone();
  const { isAdmin } = useRoles();

  const [allZones, setAllZones] = useState<string[] | null>(null);
  const [zonesError, setZonesError] = useState<string | null>(null);
  const [now, setNow] = useState(() => new Date());

  // Keep the live preview ticking so the effect of a change is obvious.
  useEffect(() => {
    const timer = window.setInterval(() => setNow(new Date()), 1000);
    return () => window.clearInterval(timer);
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetchTimezones();
        if (!cancelled) setAllZones(res.timezones);
      } catch {
        if (!cancelled) {
          // The suggested list still covers the common cases, so this is a
          // degraded experience rather than a broken page.
          setZonesError("Could not load the full timezone list; showing common zones only.");
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const zoneOptions = useMemo(() => {
    const suggested = SUGGESTED_ZONES.filter(Boolean);
    if (!allZones) return suggested;
    const rest = allZones.filter((z) => !suggested.includes(z));
    return [...suggested, ...rest];
  }, [allZones]);

  return (
    <div className="mx-auto max-w-3xl space-y-6 p-6">
      <header>
        <h1 className="text-2xl font-bold text-content">Settings</h1>
        <p className="mt-1 text-sm text-content-muted">
          These preferences are saved to your account and follow you to any browser you sign in
          from.
        </p>
      </header>

      {error && (
        <div
          role="alert"
          className="rounded-lg border border-warning-border bg-warning-subtle p-4 text-sm text-warning-fg"
        >
          {error}
        </div>
      )}

      {clockSkewMs !== null && (
        <div
          role="alert"
          className="rounded-lg border border-warning-border bg-warning-subtle p-4 text-sm text-warning-fg"
        >
          This device&rsquo;s clock is about {Math.round(Math.abs(clockSkewMs) / 60000)} minutes{" "}
          {clockSkewMs > 0 ? "ahead of" : "behind"} the server. Relative times such as
          &ldquo;5 minutes ago&rdquo; may be misleading until it is corrected.
        </div>
      )}

      <Section
        title="Theme"
        description="Torrens Dark is the default. Your choice applies only to your account."
      >
        <div className="grid gap-3 sm:grid-cols-3">
          {THEMES.map((theme) => {
            const selected = preferences.theme === theme.id;
            return (
              <button
                key={theme.id}
                type="button"
                aria-pressed={selected}
                onClick={() => update({ theme: theme.id as ThemeId })}
                className={`rounded-xl border p-4 text-left transition-colors ${
                  selected
                    ? "border-accent bg-accent-subtle"
                    : "border-edge bg-surface hover:bg-surface-hover"
                }`}
              >
                <div className="flex gap-1.5" aria-hidden="true">
                  {theme.swatch.map((colour) => (
                    <span
                      key={colour}
                      className="h-6 w-6 rounded-md border border-edge"
                      style={{ backgroundColor: colour }}
                    />
                  ))}
                </div>
                <div className="mt-3 text-sm font-semibold text-content">{theme.label}</div>
                <div className="mt-1 text-xs text-content-muted">{theme.description}</div>
              </button>
            );
          })}
        </div>
      </Section>

      <Section
        title="Timezone"
        description="All dates and times in the application, including exported evidence, are shown in this timezone."
      >
        {zonesError && (
          <p className="mb-3 text-sm text-warning-fg" role="alert">
            {zonesError}
          </p>
        )}

        <label className="block text-sm font-medium text-content" htmlFor="timezone-select">
          Timezone
        </label>
        <select
          id="timezone-select"
          value={preferences.timezone}
          onChange={(e) => update({ timezone: e.target.value })}
          className="mt-1.5 w-full rounded-lg border border-edge-strong bg-surface px-3 py-2 text-sm text-content focus:border-accent focus:outline-none focus:ring-2 focus:ring-accent/40"
        >
          <option value={AUTO_TIMEZONE}>
            Automatic — follow this device ({detected})
          </option>
          {zoneOptions.map((zone) => (
            <option key={zone} value={zone}>
              {zone} (UTC{offsetLabel(zone, now)})
            </option>
          ))}
        </select>

        <dl className="mt-4 space-y-1.5 text-sm">
          <div className="flex justify-between gap-4">
            <dt className="text-content-muted">Currently showing times in</dt>
            <dd className="font-mono text-content">
              {effectiveTimezone} ({timezoneAbbreviation(effectiveTimezone, now)})
            </dd>
          </div>
          <div className="flex justify-between gap-4">
            <dt className="text-content-muted">Right now, that is</dt>
            <dd className="font-mono text-content">{formatDateTimeWithZone(now, format, true)}</dd>
          </div>
          <div className="flex justify-between gap-4">
            <dt className="text-content-muted">Stored in the audit record as (UTC)</dt>
            <dd className="font-mono text-content-muted">{now.toISOString()}</dd>
          </div>
        </dl>
      </Section>

      <Section title="Date and time format" description="Applies to on-screen values and CSV exports.">
        <div className="grid gap-5 sm:grid-cols-2">
          <div>
            <label className="block text-sm font-medium text-content" htmlFor="date-format-select">
              Date format
            </label>
            <select
              id="date-format-select"
              value={preferences.dateFormat}
              onChange={(e) => update({ dateFormat: e.target.value as DateFormatId })}
              className="mt-1.5 w-full rounded-lg border border-edge-strong bg-surface px-3 py-2 text-sm text-content focus:border-accent focus:outline-none focus:ring-2 focus:ring-accent/40"
            >
              {DATE_FORMAT_OPTIONS.map((opt) => (
                <option key={opt.id} value={opt.id}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-content" htmlFor="time-format-select">
              Time format
            </label>
            <select
              id="time-format-select"
              value={preferences.timeFormat}
              onChange={(e) => update({ timeFormat: e.target.value as TimeFormatId })}
              className="mt-1.5 w-full rounded-lg border border-edge-strong bg-surface px-3 py-2 text-sm text-content focus:border-accent focus:outline-none focus:ring-2 focus:ring-accent/40"
            >
              {TIME_FORMAT_OPTIONS.map((opt) => (
                <option key={opt.id} value={opt.id}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>
        </div>
      </Section>

      {isAdmin && (
        <>
          <header className="pt-2">
            <h2 className="text-lg font-bold text-content">Administration</h2>
            <p className="mt-1 text-sm text-content-muted">
              These apply to every user of the application, not just your account.
            </p>
          </header>
          <ServiceIdentitySection format={format} />
        </>
      )}

      <div className="flex justify-end">
        <button
          type="button"
          onClick={reset}
          className="rounded-lg border border-edge-strong px-4 py-2 text-sm font-semibold text-content hover:bg-surface-hover"
        >
          Reset to defaults
        </button>
      </div>
    </div>
  );
}
